import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import Helmet from "react-helmet";
import { RouteComponentProps } from "react-router";
import { useState as useWorkflowState } from "../../../context/reports/workflow";
import { HeadBar } from "../../../components/Reports";
import {
  StyledBackgroundRow,
  StyledWrap,
} from "../../../components/Reports/Reports.styles";
import { composeSites } from "../../../components/Reports/Workflow/utils";
import { useWorkflowEffects } from "./hooks/useWorkflowEffects";
import { useChart } from "../../../components/Reports/Workflow/hooks/useChart";
import { WFPortfolio } from "../../../components/Reports/Workflow";
import { useActions as useWorkflowActions } from "../../../context/reports/workflow/useActions";
import { navigate } from "../../../utils/routes/navigate";
import { useTrackedActions } from "../../../components/Reports/Workflow/hooks/useTrackedActions";
import { WORKFLOW_PERSIST_KEY } from "../../../context/reports/workflow/constants";
import { updateIsMultiSiteRoute } from "../../../redux/modules/_BETA/selections";
import EventEmitter from "../../../utils/eventEmitter";

interface MatchParams {
  siteId: string;
  teamId: string;
}

const mapStateToProps = (state: Insite.AppState) => ({
  selectedSites: state.betaSelections.selectedSites,
  userSites: state.userSites.sites,
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setMultiSite: () => dispatch(updateIsMultiSiteRoute(true)),
  };
};

type ConnectedProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = ConnectedProps & DispatchProps & RouteComponentProps<MatchParams>;

const WorkflowScene: React.FunctionComponent<Props> = ({
  selectedSites,
  userSites,
  match,
  setMultiSite,
}) => {
  const state = useWorkflowState();
  const { bars } = useChart(match);
  const { setSites } = useWorkflowActions();
  const { trackedTimeFrame } = useTrackedActions();

  /**
   * Effects for triggering when to request/update
   * data based on metricType and active sites
   */
  useWorkflowEffects();

  // ! Uncomment with re-introduction of MetricsType.opened
  /**
   * If MetricsType changes, re-request metrics data
   */
  // const handleMetricsChange = React.useCallback(function handleMetricsChange (
  //   sites: Set<string>,
  //   startDate: string,
  //   endDate: string
  // ) {
  //   return (type: MetricsType) => {
  //     void getMetrics(type, sites, startDate, endDate)
  //   }
  // }, [])

  /**
   * Update sites + metrics if:
   * 1. sites aren't yet populated in state
   * 2. sites have changed since initial load
   */
  React.useEffect(() => {
    // * If only one site is selected, re-route straight to the team breakdown view (WFTeam scene),
    // * and return before calculating composed sites
    if (selectedSites.length === 1) {
      navigate(
        `/reports/performance/workflow/site/${selectedSites[0]}`,
        "replace"
      )();
      return;
    } else {
      setMultiSite();
    }

    if (
      (state.sites.size === 0 && selectedSites.length > 0) ||
      selectedSites.length !== state.sites.size ||
      selectedSites.some((site: string) => state.sites.get(site) === undefined)
    ) {
      setSites(composeSites(selectedSites, userSites));
    }
  }, [selectedSites]);

  // * Avoid flash of portfolio view if we're about to redirect to WFTeam scene
  if (selectedSites.length === 1) return null;

  return (
    <>
      <Helmet title="Performance - Workflow" />
      <StyledBackgroundRow>
        <HeadBar
          subTitle="Workflow Performance"
          groupName="" // * TODO: Implement correct project name once single project work complete
          defaultTimeFrame={state.timeFrameOption}
          onTimeFrameChange={trackedTimeFrame}
          persistKey={WORKFLOW_PERSIST_KEY}
        />
      </StyledBackgroundRow>
      <StyledWrap>
        {/*
          // ! Uncomment with re-introduction of MetricsType.opened
          <Sidebar.Container>
            <Sidebar.Section>
              <Sidebar.Heading>Reports</Sidebar.Heading>
              <ActionsBar
                onMetricsTypeChange={handleMetricsChange(state.active, state.startDate, state.endDate)}
              />
            </Sidebar.Section>
          </Sidebar.Container>
        */}
        <WFPortfolio
          bars={bars}
          eventEmitter={new EventEmitter()}
        />
      </StyledWrap>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowScene);
